import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, X, FileUp, Sparkles } from "lucide-react";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { useUser } from "../../../foundation/altzero_base/base/contextapi/UserContext";
import { useToast } from "../../../foundation/altzero_base/base/hooks/use-toast";
import ScopingAILayout from "../components/ScopingAILayout";
import { DocumentPreview, UploadDialog } from "../components/documents/library";
import { TemplateCard } from "../components/documents/library/TemplateCard";
import { HTMLTemplateModal } from "../components/documents/library/HTMLTemplateModal";
import AdvancedTemplateUploadDialog from "../components/templates/AdvancedTemplateUploadDialog";
import { HTML_TEMPLATES, HTMLTemplate } from "../templates/html/templates-index";
import SuperLoader from "../components/common/SuperLoader";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  getAllTemplates,
  deleteCustomTemplate,
  duplicateTemplate,
} from "../services/documentLibraryService";
import { ProcessedDocument, TemplateDocument } from "../types/document-library";
import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";

export default function DocumentsLibrary() {
  const { user } = useUser();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isTemplateSelection = searchParams.get("mode") === "select";

  // Template selection callback
  const onTemplateSelect = (templateId: string) => {
    if (isTemplateSelection) {
      // Store the selected template in sessionStorage to preserve it
      sessionStorage.setItem("selectedTemplateFromLibrary", templateId);

      // Navigate back to the template selection step instead of creating new document
      navigate("/tender/documents/new", {
        replace: true,
        state: {
          returnFromLibrary: true,
          selectedTemplate: templateId,
        },
      });
    } else {
      // For normal usage, navigate to scoping AI with the template
      navigate(`/tender/documents/new?template=${templateId}`);
    }
  };

  // State
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [processedDocument, setProcessedDocument] =
    useState<ProcessedDocument | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isTemplateUploading, setIsTemplateUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Template preview modal state
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] =
    useState<TemplateDocument | null>(null);

  // Advanced upload dialog state
  const [showAdvancedUpload, setShowAdvancedUpload] = useState(false);

  // HTML Template modal state
  const [showHTMLTemplateModal, setShowHTMLTemplateModal] = useState(false);
  const [selectedHTMLTemplate, setSelectedHTMLTemplate] = useState<HTMLTemplate | null>(null);

  // Template documents state
  const [templateDocuments, setTemplateDocuments] = useState<
    TemplateDocument[]
  >([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(null);

  // GrapesJS templates state
  const [grapesJSTemplates, setGrapesJSTemplates] = useState<TemplateDocument[]>([]);

  // Load all templates on component mount with perfect consistency
  useEffect(() => {
    console.log('🔄 DocumentsLibrary: useEffect triggered', {
      hasUser: !!user,
      userId: user?.id,
      isLoadingTemplates,
      hasInitialized
    });

    if (user?.id && !hasInitialized) {
      console.log('🚀 User available, loading templates...');
      setHasInitialized(true);
      loadAllTemplates();
    } else if (user === null && !hasInitialized) {
      // User is explicitly null (not authenticated)
      console.log('❌ User not authenticated, stopping loading');
      setHasInitialized(true);
      setIsLoadingTemplates(false);
    }
    // If user is undefined, keep loading state true (still checking auth)
  }, [user, hasInitialized]);

  // 🚨 SAFETY: Timeout to prevent infinite loading
  useEffect(() => {
    const safetyTimeout = setTimeout(() => {
      if (isLoadingTemplates && !hasInitialized) {
        console.log('⚠️ Safety timeout: Stopping loading after 10 seconds');
        setIsLoadingTemplates(false);
        setHasInitialized(true);
        toast({
          title: "Loading Timeout",
          description: "Templates are taking longer than expected to load",
          variant: "destructive",
        });
      }
    }, 10000); // 10 second safety timeout

    return () => clearTimeout(safetyTimeout);
  }, [isLoadingTemplates, hasInitialized]);

  // Handle escape key to close template preview modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showTemplatePreview) {
        setShowTemplatePreview(false);
        setPreviewTemplate(null);
      }
    };

    if (showTemplatePreview) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [showTemplatePreview]);

  // Create new GrapesJS template
  const handleCreateGrapesJSTemplate = async () => {
    try {
      if (!user?.id) {
        toast({
          title: "Error",
          description: "User not authenticated",
          variant: "destructive",
        });
        return;
      }

      console.log('🆕 Creating new GrapesJS template...');

      // Generate unique template name
      const randomId = Math.random().toString(36).substring(2, 9);
      const templateName = `choice_energy_template_${randomId}`;

      // Create empty project structure
      const emptyProject = {
        pages: [],
        styles: [],
        assets: []
      };

      // Create template data
      const projectData = {
        templateName: templateName,
        savedAt: new Date().toISOString(),
        userId: user.id,
        project: emptyProject
      };

      const projectJson = JSON.stringify(projectData, null, 2);
      const blob = new Blob([projectJson], { type: 'application/json' });

      // Upload to Supabase
      const fileName = 'template.json';
      const filePath = `templates/${user.id}/${templateName}/${fileName}`;

      console.log('📁 Creating template:', templateName);
      console.log('📁 Upload path:', filePath);

      const { error } = await supabase.storage
        .from('grapesjs_templates')
        .upload(filePath, blob, {
          cacheControl: '3600',
          upsert: false, // Don't overwrite
          contentType: 'application/json'
        });

      if (error) {
        console.error('❌ Error creating template:', error);
        toast({
          title: "Error",
          description: "Failed to create template",
          variant: "destructive",
        });
        return;
      }

      console.log('✅ Template created successfully:', templateName);

      toast({
        title: "Success",
        description: "Template created successfully",
      });

      // Navigate to editor with template name
      navigate(`/tender/resource-library/document-editor?template=${templateName}`);
    } catch (error) {
      console.error('❌ Error in handleCreateGrapesJSTemplate:', error);
      toast({
        title: "Error",
        description: "Failed to create template",
        variant: "destructive",
      });
    }
  };

  // Load GrapesJS templates from Supabase storage
  const loadGrapesJSTemplates = async () => {
    try {
      if (!user?.id) {
        console.log("⏳ Waiting for user to load GrapesJS templates...");
        return;
      }

      console.log("🎨 Loading GrapesJS templates for user:", user.id);

      // List all template folders for this user
      const { data: folders, error: foldersError } = await supabase.storage
        .from('grapesjs_templates')
        .list(`templates/${user.id}`);

      if (foldersError) {
        console.error("❌ Error listing GrapesJS template folders:", foldersError);
        return;
      }

      if (!folders || folders.length === 0) {
        console.log("ℹ️ No GrapesJS templates found");
        setGrapesJSTemplates([]);
        return;
      }

      console.log(`📁 Found ${folders.length} GrapesJS template folders`);

      // For each template folder, load the template.json file
      const grapesTemplates: TemplateDocument[] = [];

      for (const folder of folders) {
        if (!folder.name) continue;

        const templateName = folder.name;
        const fileName = 'template.json';
        const filePath = `templates/${user.id}/${templateName}/${fileName}`;

        // Download the template file with aggressive cache busting
        const { data: urlData } = supabase.storage
          .from('grapesjs_templates')
          .getPublicUrl(filePath);

        // Add timestamp and random to bust cache aggressively
        const cacheBuster = `?t=${Date.now()}&nocache=${Math.random()}`;
        const urlWithCacheBuster = urlData.publicUrl + cacheBuster;

        console.log(`📥 Fetching template: ${templateName} (aggressive cache-busting)`);

        // Fetch with cache busting and no-cache headers
        const response = await fetch(urlWithCacheBuster, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });

        if (!response.ok) {
          console.log(`⚠️ Template file not found for: ${templateName}`);
          continue;
        }

        // Parse the JSON content
        const fileContent = await response.text();
        const templateData = JSON.parse(fileContent);

        console.log(`✅ Loaded template: ${templateName}, saved at: ${templateData.savedAt}`);

        // Create TemplateDocument object
        grapesTemplates.push({
          id: `grapesjs_${templateName}`,
          title: templateData.templateName || templateName,
          description: 'GrapesJS Template',
          date: new Date(templateData.savedAt).toLocaleDateString(),
          sections: [],
          rawData: {
            content: "",
            sections: [],
            metadata: {
              isGrapesJSTemplate: true,
              templateName: templateName,
              userId: templateData.userId,
              savedAt: templateData.savedAt,
              projectData: templateData.project
            }
          }
        });
      }

      console.log(`✅ Loaded ${grapesTemplates.length} GrapesJS templates`);
      setGrapesJSTemplates(grapesTemplates);
    } catch (error) {
      console.error("❌ Error loading GrapesJS templates:", error);
      setGrapesJSTemplates([]);
    }
  };

  // Load all templates with professional consistency
  const loadAllTemplates = async () => {
    const startTime = Date.now();

    try {
      setIsLoadingTemplates(true);

      // Only load templates if user is available
      if (!user?.id) {
        console.log("⏳ Waiting for user to load templates...");
        setIsLoadingTemplates(false);
        return;
      }

      console.log("🔄 Loading templates for user:", user.id);

      // Load both regular templates and GrapesJS templates
      const [allTemplates] = await Promise.all([
        getAllTemplates(user.id),
        loadGrapesJSTemplates()
      ]);

      // Convert LibraryDocument to TemplateDocument format
      const convertedTemplates: TemplateDocument[] = allTemplates.map(
        (template) => ({
          id: String(template.id),
          title: template.title,
          description:
            template.rawData?.metadata?.description ||
            template.type ||
            "Template",
          date: template.date,
          sections: template.sections || [],
          rawData: template.rawData || {
            content: "",
            sections: [],
            metadata: {},
          },
        })
      );

      console.log("✅ Templates loaded successfully:", convertedTemplates.length);

      // 🚨 PROFESSIONAL: Ensure minimum loading time for smooth UX (prevent flashing)
      const elapsedTime = Date.now() - startTime;
      const minimumLoadTime = 800; // 800ms minimum for professional feel

      if (elapsedTime < minimumLoadTime) {
        console.log(`⏳ Adding ${minimumLoadTime - elapsedTime}ms delay for smooth UX`);
        await new Promise(resolve => setTimeout(resolve, minimumLoadTime - elapsedTime));
      }

      setTemplateDocuments(convertedTemplates);
    } catch (error) {
      console.error("❌ Error loading templates:", error);
      toast({
        title: "Error",
        description: "Failed to load templates",
        variant: "destructive",
      });
      setTemplateDocuments([]);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  // Reload templates without showing SuperLoader (for delete, duplicate, etc.)
  const reloadTemplatesQuietly = async () => {
    try {
      if (!user?.id) return;

      console.log("🔄 Quietly reloading templates...");
      const allTemplates = await getAllTemplates(user.id);

      const convertedTemplates: TemplateDocument[] = allTemplates.map(
        (template) => ({
          id: String(template.id),
          title: template.title,
          description:
            template.rawData?.metadata?.description ||
            template.type ||
            "Template",
          date: template.date,
          sections: template.sections || [],
          rawData: template.rawData || {
            content: "",
            sections: [],
            metadata: {},
          },
        })
      );

      setTemplateDocuments(convertedTemplates);
      console.log("✅ Templates reloaded quietly:", convertedTemplates.length);
    } catch (error) {
      console.error("❌ Error reloading templates:", error);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      setDeletingTemplateId(templateId);
      console.log('🗑️ Deleting template:', templateId);

      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      await deleteCustomTemplate(templateId, user.id);

      toast({
        title: "Success",
        description: "Template deleted successfully",
      });

      // Use quiet reload - no SuperLoader
      await reloadTemplatesQuietly();

      console.log('✅ Template deleted successfully');
    } catch (error) {
      console.error("❌ Error deleting template:", error);
      toast({
        title: "Error",
        description: "Failed to delete template",
        variant: "destructive",
      });
    } finally {
      setDeletingTemplateId(null);
    }
  };

  // Handle template preview
  const handleTemplatePreview = (templateId: string) => {
    const template = templateDocuments.find((t) => t.id === templateId);
    if (template) {
      setPreviewTemplate(template);
      setShowTemplatePreview(true);
    }
  };

  // Handle template edit (only for custom templates)
  const handleTemplateEdit = (templateId: string) => {
    const template = templateDocuments.find((t) => t.id === templateId);
    if (template && !(template as any).isDefault) {
      // Navigate to template creation page with edit mode
      navigate(`/tender/templates/create?edit=true`, {
        state: {
          editTemplate: {
            id: template.id,
            title: template.title,
            description: template.description || "",
            content: template.rawData?.content || "",
          },
        },
      });
    } else {
      toast({
        title: "Cannot Edit",
        description: "Default templates cannot be edited",
        variant: "destructive",
      });
    }
  };

  // Handle template duplication (for all templates)
  const handleTemplateDuplicate = async (templateId: string) => {
    if (!user?.id) {
      toast({
        title: "Error",
        description: "User not authenticated",
        variant: "destructive",
      });
      return;
    }

    try {
      const template = templateDocuments.find((t) => t.id === templateId);
      if (!template) {
        toast({
          title: "Template Not Found",
          description: "The selected template could not be found",
          variant: "destructive",
        });
        return;
      }

      // Create duplicate without "(Copy)" suffix
      await duplicateTemplate(
        templateId,
        {
          name: template.title,
          description: template.description,
          content: template.rawData?.content,
          sections: template.sections,
        },
        user.id
      );

      // Refresh templates quietly - no SuperLoader
      await reloadTemplatesQuietly();

      toast({
        title: "Success",
        description: "Template duplicated successfully",
      });
    } catch (error) {
      console.error("Error duplicating template:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to duplicate template",
        variant: "destructive",
      });
    }
  };

  // Handle when processed document is ready
  const handleProcessedDocument = (document: ProcessedDocument) => {
    setProcessedDocument(document);
    setShowPreview(true);
  };

  // Handle MD file import - Navigate to template creation page
  const handleMdImport = async (file: File) => {
    try {
      // Read the MD file content
      const content = await file.text();

      // Extract title from filename (remove .md extension)
      const title = file.name.replace(/\.md$/, "");

      // Navigate to template creation page with MD content
      navigate(`/tender/templates/create?import=true`, {
        state: {
          importedContent: content,
          importedTitle: title,
          importedFileName: file.name,
        },
      });

      toast({
        title: "Success",
        description: "MD file imported successfully",
      });
    } catch (error) {
      console.error("Error importing MD file:", error);
      toast({
        title: "Error",
        description: "Failed to import MD file",
        variant: "destructive",
      });
    }
  };

  // Handle template upload (PDF/DOCX) with progress and auto-open
  const handleTemplateUpload = async (file: File) => {
    if (!user?.id) {
      toast({
        title: "Error",
        description: "User not authenticated",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsTemplateUploading(true);
      setUploadProgress(0);
      // DON'T close dialog - keep it open to show progress

      // Show initial progress
      setUploadProgress(10);

      console.log('🚀 Starting template upload:', {
        fileName: file.name,
        fileSize: file.size,
        userId: user.id
      });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name.replace(/\.(pdf|docx)$/i, ''));
      formData.append('description', `Template imported from ${file.name}`);

      // Simulate progress during upload
      setUploadProgress(30);

      const response = await fetch('/api/tender/templates/upload', {
        method: 'POST',
        headers: {
          'x-api-key': 'scopingai',
          'x-user-id': user.id,
        },
        body: formData,
      });

      setUploadProgress(70);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const result = await response.json();
      setUploadProgress(90);

      console.log('✅ Template upload successful:', {
        templateId: result.template.id,
        templateName: result.template.name,
        sectionsCount: result.template.sections
      });

      toast({
        title: "Upload Complete!",
        description: `Template "${result.template.name}" uploaded successfully with ${result.template.sections} sections`,
      });

      setUploadProgress(100);

      // Reload templates quietly to show the new one - no SuperLoader
      await reloadTemplatesQuietly();

      // Close upload dialog after successful upload
      setShowAdvancedUpload(false);

      // 🚨 AUTO-OPEN: Navigate to edit the uploaded template
      setTimeout(() => {
        console.log('🔄 Auto-opening uploaded template for editing', {
          templateId: result.template.id,
          templateName: result.template.name,
          hasContent: !!result.template.content,
          contentPreview: result.template.content ? result.template.content.substring(0, 100) : 'no content'
        });

        navigate("/tender/templates/create?edit=true", {
          state: {
            editTemplate: {
              id: result.template.id,
              title: result.template.name,
              description: result.template.description,
              content: result.template.content,
              category: result.template.category || "general"
            }
          }
        });
      }, 1000); // Small delay to show completion

    } catch (error) {
      console.error("Error uploading template:", error);
      toast({
        title: "Upload Failed",
        description: error instanceof Error ? error.message : "Failed to upload template",
        variant: "destructive",
      });
    } finally {
      setTimeout(() => {
        setIsTemplateUploading(false);
        setUploadProgress(0);
      }, 1500);
    }
  };

  // Handle template saving for uploaded documents
  const handleSaveTemplate = async () => {
    if (!processedDocument || !showPreview) return;

    try {
      setIsUploading(true);

      // Navigate to template creation page with the processed content
      navigate("/tender/templates/create", {
        state: {
          importedContent: processedDocument.fullContent,
          importedTitle: processedDocument.title,
        },
      });

      toast({
        title: "Success",
        description: "Document processed successfully",
      });

      setShowPreview(false);
      setProcessedDocument(null);
      setIsUploadDialogOpen(false);
    } catch (error) {
      console.error("Error processing template:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to process template",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <ScopingAILayout>
      <div className="container mx-auto px-6 py-8">
        <div className="space-y-6">
          <div>
            {isTemplateSelection ? (
              <button
                onClick={() => navigate("/tender/documents/new")}
                className="flex items-center text-muted-foreground hover:text-foreground mb-4"
              >
                <ArrowLeft size={16} className="mr-2" />
                Back to Template Selection
              </button>
            ) : (
              <Link
                to="/tender/resource-library"
                className="flex items-center text-muted-foreground hover:text-foreground mb-4"
              >
                <ArrowLeft size={16} className="mr-2" />
                Back to Resource Library
              </Link>
            )}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold">
                  {isTemplateSelection ? "Select Template" : "Templates"}
                </h1>
                <p className="text-muted-foreground mt-1">
                  {isTemplateSelection
                    ? "Choose a template for your proposal"
                    : "Manage and organize your proposal templates"}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowAdvancedUpload(true)}
                  disabled={isTemplateUploading}
                  className="gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  {isTemplateUploading ? "Uploading..." : "Upload Sample Template"}
                </Button>
                <Button onClick={handleCreateGrapesJSTemplate}>
                  Create Template
                </Button>
              </div>
            </div>
          </div>

          {/* Templates Grid */}
          {isLoadingTemplates ? (
            <SuperLoader
              title="Loading Templates"
              description="Fetching your templates and organizing them for you..."
            />
          ) : templateDocuments.length === 0 && grapesJSTemplates.length === 0 ? (
            <div className="text-center py-12 border rounded-lg bg-muted/30">
              <p className="text-muted-foreground mb-4">No templates found</p>
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowAdvancedUpload(true)}
                  disabled={isTemplateUploading}
                  className="gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  {isTemplateUploading ? "Processing..." : "Upload Sample Template"}
                </Button>
                <Button onClick={handleCreateGrapesJSTemplate}>
                  Create your first template
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {/* GrapesJS Templates */}
              {grapesJSTemplates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onPreview={() => {
                    // Preview GrapesJS template in same tab
                    const templateName = (template.rawData?.metadata as any)?.templateName;
                    if (templateName) {
                      // Navigate to editor in same tab
                      navigate(`/tender/resource-library/document-editor?template=${templateName}`);
                    }
                  }}
                  onEdit={() => {
                    // Edit in GrapesJS editor
                    const templateName = (template.rawData?.metadata as any)?.templateName;
                    if (templateName) {
                      navigate(`/tender/resource-library/document-editor?template=${templateName}`);
                    }
                  }}
                  onDelete={async () => {
                    // Delete GrapesJS template
                    const templateName = (template.rawData?.metadata as any)?.templateName;
                    if (templateName && user?.id) {
                      try {
                        setDeletingTemplateId(template.id);

                        // Delete the template file
                        const fileName = 'template.json';
                        const filePath = `templates/${user.id}/${templateName}/${fileName}`;

                        await supabase.storage
                          .from('grapesjs_templates')
                          .remove([filePath]);

                        toast({
                          title: "Success",
                          description: "Template deleted successfully",
                        });

                        // Reload templates
                        await loadGrapesJSTemplates();
                      } catch (error) {
                        console.error("Error deleting GrapesJS template:", error);
                        toast({
                          title: "Error",
                          description: "Failed to delete template",
                          variant: "destructive",
                        });
                      } finally {
                        setDeletingTemplateId(null);
                      }
                    }
                  }}
                  onSelect={(id) => {
                    // Select template for use
                    onTemplateSelect(id);
                  }}
                  isSelectionMode={isTemplateSelection}
                  isDeleting={deletingTemplateId === template.id}
                />
              ))}

              {/* HTML Templates from Registry */}
              {HTML_TEMPLATES.map((htmlTemplate) => (
                <TemplateCard
                  key={htmlTemplate.id}
                  template={{
                    id: htmlTemplate.id,
                    title: htmlTemplate.name,
                    description: htmlTemplate.description,
                    date: new Date().toLocaleDateString(),
                    sections: htmlTemplate.sections,
                    rawData: {
                      content: "",
                      sections: [],
                      metadata: {
                        isHTMLTemplate: true,
                        filePath: htmlTemplate.filePath,
                        category: htmlTemplate.category,
                      } as any,
                    },
                  }}
                  onPreview={() => {
                    setSelectedHTMLTemplate(htmlTemplate);
                    setShowHTMLTemplateModal(true);
                  }}
                  onEdit={() => {
                    setSelectedHTMLTemplate(htmlTemplate);
                    setShowHTMLTemplateModal(true);
                  }}
                  onSelect={() => {
                    setSelectedHTMLTemplate(htmlTemplate);
                    setShowHTMLTemplateModal(true);
                  }}
                  isSelectionMode={isTemplateSelection}
                />
              ))}

              {/* Regular Templates */}
              {templateDocuments.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onPreview={handleTemplatePreview}
                  onEdit={handleTemplateEdit}
                  onDelete={handleDeleteTemplate}
                  onSelect={onTemplateSelect}
                  onDuplicate={handleTemplateDuplicate}
                  isSelectionMode={isTemplateSelection}
                  isDeleting={deletingTemplateId === template.id}
                />
              ))}
            </div>
          )}

          {/* Upload Dialog */}
          <UploadDialog
            isOpen={isUploadDialogOpen}
            onClose={() => setIsUploadDialogOpen(false)}
            onDocumentProcessed={handleProcessedDocument}
          />

          {/* Document Preview Dialog */}
          {showPreview && processedDocument && (
            <DocumentPreview
              processedDocument={processedDocument}
              onSave={handleSaveTemplate}
              onCancel={() => {
                setShowPreview(false);
                setProcessedDocument(null);
              }}
              isLoading={isUploading}
            />
          )}

          {/* Template Preview Modal */}
          {showTemplatePreview && previewTemplate && (
            <div
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setShowTemplatePreview(false);
                  setPreviewTemplate(null);
                }
              }}
            >
              <div className="bg-background rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground">
                      {previewTemplate.title}
                    </h2>
                    {previewTemplate.description && (
                      <p className="text-muted-foreground mt-1">
                        {previewTemplate.description}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowTemplatePreview(false);
                      setPreviewTemplate(null);
                    }}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {/* Modal Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-em:text-foreground prose-code:text-foreground prose-pre:text-foreground prose-blockquote:text-muted-foreground prose-li:text-foreground">
                    {previewTemplate.rawData?.content ? (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({ children }) => (
                            <h1 className="text-3xl font-bold mb-6 text-foreground border-b-2 border-primary pb-3 mt-8 first:mt-0">
                              {children}
                            </h1>
                          ),
                          h2: ({ children }) => (
                            <h2 className="text-2xl font-semibold mb-4 mt-8 text-foreground border-l-4 border-primary pl-4">
                              {children}
                            </h2>
                          ),
                          h3: ({ children }) => (
                            <h3 className="text-xl font-medium mb-3 mt-6 text-foreground">
                              {children}
                            </h3>
                          ),
                          h4: ({ children }) => (
                            <h4 className="text-lg font-medium mb-2 mt-4 text-foreground">
                              {children}
                            </h4>
                          ),
                          p: ({ children }) => (
                            <p className="mb-4 text-foreground leading-relaxed">
                              {children}
                            </p>
                          ),
                          ul: ({ children }) => (
                            <ul className="mb-4 ml-6 list-disc text-foreground">
                              {children}
                            </ul>
                          ),
                          ol: ({ children }) => (
                            <ol className="mb-4 ml-6 list-decimal text-foreground">
                              {children}
                            </ol>
                          ),
                          li: ({ children }) => (
                            <li className="mb-1 text-foreground">{children}</li>
                          ),
                          blockquote: ({ children }) => (
                            <blockquote className="border-l-4 border-muted pl-4 italic text-muted-foreground mb-4">
                              {children}
                            </blockquote>
                          ),
                          code: ({ children }) => (
                            <code className="bg-muted px-1 py-0.5 rounded text-sm font-mono text-foreground">
                              {children}
                            </code>
                          ),
                          pre: ({ children }) => (
                            <pre className="bg-muted p-4 rounded-lg overflow-x-auto mb-4">
                              {children}
                            </pre>
                          ),
                          img: ({ src, alt, ...props }) => (
                            <div className="my-6 text-center">
                              <img
                                src={src}
                                alt={alt || 'Document image'}
                                className="max-w-full h-auto mx-auto rounded-lg shadow-lg border border-border"
                                style={{ maxHeight: '600px' }}
                                onError={(e) => {
                                  console.error('Image failed to load:', src);
                                  e.currentTarget.style.display = 'none';
                                }}
                                {...props}
                              />
                              {alt && (
                                <div className="text-sm text-muted-foreground mt-2 italic">
                                  {alt}
                                </div>
                              )}
                            </div>
                          ),
                          hr: () => (
                            <div className="my-8 border-t-2 border-border opacity-50"></div>
                          ),
                        }}
                      >
                        {previewTemplate.rawData.content}
                      </ReactMarkdown>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>No content available for this template.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex justify-end gap-2 p-6 border-t bg-muted/30 flex-shrink-0">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowTemplatePreview(false);
                      setPreviewTemplate(null);
                    }}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Advanced Template Upload Dialog */}
        <AdvancedTemplateUploadDialog
          isOpen={showAdvancedUpload}
          onClose={() => setShowAdvancedUpload(false)}
          onWordUpload={handleTemplateUpload}
          onMarkdownUpload={handleMdImport}
          isUploading={isTemplateUploading}
          uploadProgress={uploadProgress}
        />

        {/* HTML Template Modal */}
        <HTMLTemplateModal
          isOpen={showHTMLTemplateModal}
          onClose={() => {
            setShowHTMLTemplateModal(false);
            setSelectedHTMLTemplate(null);
          }}
          templateName={selectedHTMLTemplate?.name || ""}
          templateFilePath={selectedHTMLTemplate?.filePath}
          templateId={selectedHTMLTemplate?.id}
        />
      </div>
    </ScopingAILayout>
  );
}
